'use client';

import { Box, Divider, Flex, Grid, Group } from '@mantine/core';
import { Fragment, useEffect } from 'react';
import { IconArrowRight, IconShare3 } from '@tabler/icons-react';
import { useParams, usePathname } from 'next/navigation';

import { API_ENDPOINT } from '@/config/endpoint';
import { Badge } from '@/components/Badge';
import Card from '@/components/Card';
import DMText from '@/components/DMText';
import Link from 'next/link';
import ShareButton from '@/components/Buttons/ShareButton';
import TechstackAvatarBadgeGroup from '@/components/TechstackAvatarBadgeGroup';
import { isDark } from '@/utils/utils';
import { mixpanelTrack } from '@/utils/mixpanel';
import { sendGTMEvent } from '@next/third-parties/google';
import themeVariables from '@/styles/themeVariables';
import { useLatestNewsPageStyle } from '@/app/latest-news/page.style';
import { useQuery } from '@tanstack/react-query';
import { useScrollInit } from '@/utils/hook';

const LatestNewsDetailPage = () => {
  const { dark } = isDark();

  const { id } = useParams();
  const { classes } = useLatestNewsPageStyle();

  const { data } = useQuery(
    ['latest-news', id],
    () => API_ENDPOINT.news.getNewsDetail(+id),
    {
      suspense: true,
    }
  );

  useEffect(() => {
    mixpanelTrack('NewsDetailPage', 'Viewed', { id: id });
    useScrollInit();
  }, [id]);

  useEffect(() => {
    sendGTMEvent({ event: 'page__view' });
  }, []);

  const path = usePathname();

  if (data?.name === 'AxiosError') return null;
  return (
    <Grid columns={24} justify={'center'}>
      <Grid.Col xl={14} lg={16} md={22} xs={22} sm={22}>
        <Card noShadow>
          <div className={classes.newsPageContainer}>
            <Group>
              <Badge
                text={'공유된 기사'}
                color={themeVariables.colors.blue200}
                backgroundColor={themeVariables.colors.blue100}
                icon={<IconShare3 size={14} />}
              />
              <Box ml={'auto'}>
                <ShareButton
                  size={21}
                  title={data?.item.title}
                  text={data?.item.summary}
                  url={process.env.NEXT_PUBLIC_BASE_URL + path}
                />
              </Box>
            </Group>
            <DMText
              py={16}
              weight={700}
              dColor={themeVariables.darkMode.strong}
              size={18}
            >
              {data?.item.title}
            </DMText>
            {data?.item?.techstacks && data?.item?.techstacks?.length > 0 && (
              <Box pb={10}>
                <TechstackAvatarBadgeGroup techstacks={data.item.techstacks} />
              </Box>
            )}
            <Divider color={themeVariables.colors.light200} my={6} />
            <DMText
              lh={2}
              py={16}
              size={14}
              weight={400}
              dColor={themeVariables.darkMode.normal}
            >
              {data?.item.summary?.split('\n').map((line, index) => (
                <Fragment key={index}>
                  {line.replace('-', '•')}
                  <br />
                </Fragment>
              ))}
            </DMText>
            <Group position={'right'} pt={32}>
              <Link href={'/latest-news'}>
                <Flex align={'center'} gap={4}>
                  <DMText
                    sx={[themeVariables.xsize.label1]}
                    dColor={themeVariables.darkMode.neutral}
                  >
                    최신 기술 뉴스 더 보기
                  </DMText>
                  <IconArrowRight
                    size={16}
                    color={themeVariables.darkMode.neutral(dark)}
                  />
                </Flex>
              </Link>
            </Group>
          </div>
        </Card>
      </Grid.Col>
    </Grid>
  );
};

export default LatestNewsDetailPage;
